import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, OverlayTrigger, Tooltip, ProgressBar, Modal,
    InputGroup, Form, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function SauvegardeRestauration() {
    const navigate = useNavigate();
    const [sauvegardes, setSauvegardes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    // États pour la recherche
    const [searchText, setSearchText] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);

    // États pour le modal de restauration
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [fileToRestore, setFileToRestore] = useState(null);
    
    // États pour le téléchargement (optionnel)
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);

    useEffect(() => {
        fetchSauvegardes();
    }, []);

    const fetchSauvegardes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=sauvegardes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSauvegardes(result.data || []);
            setCurrentPage(1); // Reset à la première page
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreClick = (fichier) => {
        setFileToRestore(fichier);
        setShowRestoreModal(true);
    };

  const handleRestoreConfirm = async () => {
    if (!fileToRestore) return;
    
    setShowRestoreModal(false);
    setRestoring(true);
    setError(null);
    setSuccess('');
    setSelectedFile(fileToRestore);
    
    try {
        const token = localStorage.getItem("token");
        
        console.log("Tentative de restauration du fichier:", fileToRestore);
        
        const response = await fetch(
            'https://sawd.julesferry-ga.com/api/parametres.php',
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    action: 'restaurer_sauvegarde',
                    fichier: fileToRestore
                })
            }
        );
        
        // ✅ Lire la réponse même en cas d'erreur
        const text = await response.text();
        console.log("Réponse brute restauration:", text);
        console.log("Status HTTP:", response.status);
        
        if (!response.ok) {
            throw new Error(`Erreur HTTP ${response.status}: ${text.substring(0, 200)}`);
        }
        
        if (!text || text.trim() === '') {
            throw new Error("La réponse du serveur est vide");
        }
        
        let result;
        try {
            result = JSON.parse(text);
        } catch (parseError) {
            console.error("Erreur de parsing JSON:", parseError);
            console.log("Contenu reçu (premiers 200 caractères):", text.substring(0, 200));
            throw new Error("Le serveur n'a pas renvoyé un JSON valide");
        }
        
        if (result.success) {
            setSuccess("✅ Restauration réussie ! La page va se recharger...");
            setTimeout(() => {
                window.location.reload();
            }, 3000);
        } else {
            setError(result.message || "Erreur lors de la restauration");
        }
    } catch (err) {
        console.error("Erreur complète:", err);
        setError(err.message);
    } finally {
        setRestoring(false);
        setSelectedFile(null);
        setFileToRestore(null);
    }
};

    const handleDownload = async (fichier) => {
        try {
            const token = localStorage.getItem("token");
            
            const downloadUrl = `https://sawd.julesferry-ga.com/api/parametres.php?action=telecharger_sauvegarde&fichier=${encodeURIComponent(fichier)}&token=${token}`;
            
            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur lors du téléchargement");
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fichier;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            setSuccess(`📥 Téléchargement de ${fichier} démarré`);
            setTimeout(() => setSuccess(''), 3000);
            
        } catch (err) {
            setError("Erreur lors du téléchargement: " + err.message);
        }
    };

    const formatTaille = (octets) => {
        if (!octets) return '-';
        if (octets < 1024) return octets + ' o';
        if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
        return (octets / (1024 * 1024)).toFixed(1) + ' Mo';
    };

    const getNiveauRisque = (date) => {
        const jours = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
        if (jours <= 7) return { color: 'success', text: 'Récente' };
        if (jours <= 30) return { color: 'warning', text: 'Mois' };
        return { color: 'danger', text: 'Ancienne' };
    };

    // Filtrage des sauvegardes
    const filteredSauvegardes = useMemo(() => {
        return sauvegardes.filter(s => 
            s.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
            formatDate(s.date).toLowerCase().includes(searchText.toLowerCase())
        );
    }, [sauvegardes, searchText]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSauvegardes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSauvegardes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des sauvegardes...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))',
                                            boxShadow: '0 10px 20px rgba(245, 158, 11, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-restart-line text-warning" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Restauration
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Restaurer une sauvegarde précédente
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={() => navigate('/parametres/sauvegarde/manuel')}
                                        className="rounded-pill px-4"
                                    >
                                        <i className="ri-database-2-line me-2"></i>Sauvegarde
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Messages */}
                    {success && (
                        <Alert variant="success" className="mb-4 alert-modern">
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                                <div>
                                    <strong className="text-success">Succès</strong>
                                    <p className="mb-0">{success}</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" className="mb-4 alert-modern">
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                                <div>
                                    <strong className="text-danger">Erreur</strong>
                                    <p className="mb-0">{error}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Avertissement */}
                    <Card className="border-0 shadow-sm mb-4 bg-warning bg-opacity-10">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-warning bg-opacity-25 rounded-circle p-3 me-3">
                                    <i className="ri-alert-line text-warning fs-2"></i>
                                </div>
                                <div>
                                    <h5 className="text-warning mb-2"> Attention - Opération irréversible</h5>
                                    <p className="mb-0 text-muted">
                                        La restauration d'une sauvegarde va remplacer toutes les données actuelles par celles de la sauvegarde sélectionnée.
                                        Cette action est définitive et ne peut pas être annulée. Assurez-vous d'avoir une sauvegarde récente de vos données actuelles.
                                    </p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Progression si restauration en cours */}
                    {restoring && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h6 className="mb-3">Restauration en cours...</h6>
                                <ProgressBar 
                                    animated 
                                    now={100} 
                                    variant="warning" 
                                    style={{ height: '10px', borderRadius: '5px' }}
                                />
                                <p className="text-muted small mt-3 mb-0">
                                    <i className="ri-information-line me-1"></i>
                                    Veuillez patienter, la restauration peut prendre quelques instants.
                                </p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Barre de recherche */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher un fichier ou une date..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {searchText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setSearchText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="text-end">
                                    <Badge bg="warning" className="px-3 py-2">
                                        {filteredSauvegardes.length} fichier(s) trouvé(s)
                                    </Badge>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des sauvegardes avec pagination */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-history-line me-2 text-warning"></i>
                                Sauvegardes disponibles
                            </h5>
                            <Badge bg="warning" className="px-3 py-2">
                                {filteredSauvegardes.length} fichier(s)
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Fichier</th>
                                            <th>Taille</th>
                                            <th>Âge</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((s, index) => {
                                                const risque = getNiveauRisque(s.date);
                                                return (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="ri-calendar-line me-2 text-muted"></i>
                                                                {formatDate(s.date)}
                                                            </div>
                                                            <small className="text-muted">
                                                                <i className="ri-time-line me-1"></i>
                                                                {new Date(s.date).toLocaleTimeString('fr-FR')}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <code className="bg-light p-2 rounded">{s.nom}</code>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary" className="rounded-pill px-3 py-2">
                                                                {formatTaille(s.taille)}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge 
                                                                bg={risque.color} 
                                                                className="rounded-pill px-3 py-2"
                                                            >
                                                                {risque.text}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Restaurer cette sauvegarde</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-warning" 
                                                                    size="sm"
                                                                    className="rounded-circle me-2"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    onClick={() => handleRestoreClick(s.nom)}
                                                                    disabled={restoring}
                                                                >
                                                                    <i className="ri-restart-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Télécharger</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="rounded-circle"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    onClick={() => handleDownload(s.nom)}
                                                                >
                                                                    <i className="ri-download-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                                    <h5>Aucune sauvegarde trouvée</h5>
                                                    <p className="text-muted">
                                                        {searchText 
                                                            ? "Aucun résultat ne correspond à votre recherche"
                                                            : "Créez d'abord une sauvegarde dans l'onglet 'Sauvegarde manuelle'"}
                                                    </p>
                                                    {searchText ? (
                                                        <Button 
                                                            variant="outline-primary"
                                                            onClick={() => setSearchText('')}
                                                        >
                                                            <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                        </Button>
                                                    ) : (
                                                        <Button 
                                                            variant="primary"
                                                            onClick={() => navigate('/parametres/sauvegarde/manuel')}
                                                            className="mt-2"
                                                        >
                                                            <i className="ri-database-2-line me-2"></i>
                                                            Créer une sauvegarde
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredSauvegardes.length)} sur {filteredSauvegardes.length} sauvegardes
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Recommandations */}
                    {sauvegardes.length > 0 && (
                        <Card className="border-0 shadow-sm mt-4 bg-light">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    <Col md={8}>
                                        <div className="d-flex align-items-center text-muted small">
                                            <i className="ri-information-line me-2 fs-5 text-info"></i>
                                            <span>
                                                <strong>Recommandation :</strong> Pour une sécurité optimale, 
                                                conservez au moins une sauvegarde par semaine et testez régulièrement 
                                                la procédure de restauration.
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4} className="text-end">
                                        <Badge bg="info" className="px-3 py-2">
                                            <i className="ri-shield-check-line me-1"></i>
                                            Bonnes pratiques
                                        </Badge>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {/* Modal de confirmation de restauration */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered className="restore-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-warning-soft">
                                        <i className="ri-alert-line text-warning" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-warning"> Attention</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Vous êtes sur le point de restaurer la sauvegarde <br />
                            <strong className="text-dark">"{fileToRestore}"</strong>
                        </p>
                        
                        <Alert variant="danger" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible !</strong>
                                    <p className="mb-0 small mt-1">
                                        La restauration va écraser TOUTES les données actuelles par celles de la sauvegarde.
                                        Assurez-vous d'avoir une sauvegarde récente de vos données actuelles.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Fichier à restaurer :</span>
                                <code className="bg-white p-2 rounded">{fileToRestore}</code>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowRestoreModal(false)}
                            className="px-4 rounded-pill"
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={handleRestoreConfirm}
                            className="px-5 rounded-pill"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                border: 'none',
                                color: 'white'
                            }}
                        >
                            <i className="ri-restart-line me-2"></i>
                            Restaurer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    .fade-in { animation: fadeIn 0.5s ease; }
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                    .alert-modern { border-radius: 12px; border-left: 4px solid; }
                    .custom-table thead th { background: #f8fafc; color: #1e293b; font-weight: 600; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; }
                    .custom-table tbody td { padding: 1rem 0.75rem; vertical-align: middle; }
                    
                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .search-input .form-control:focus {
                        box-shadow: none;
                        border-color: #dee2e6;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                    
                    .restore-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    .restore-modal .modal-header {
                        background: linear-gradient(135deg, #fff9e6, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .restore-modal .modal-body {
                        padding: 1rem 2rem;
                    }

                    .restore-modal .modal-footer {
                        border-top: 1px solid #e2e8f0;
                        padding: 1.5rem 2rem 2rem;
                        background: #f8fafc;
                    }

                    .icon-wrapper {
                        width: 100px;
                        height: 100px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        animation: pulse 2s infinite;
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.15);
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .restore-modal .btn {
                        border-radius: 12px;
                        font-weight: 600;
                        padding: 0.6rem 1.5rem;
                        transition: all 0.3s ease;
                    }

                    .restore-modal .btn:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    }

                    .restore-modal .alert {
                        border: none;
                        border-radius: 12px;
                        background: linear-gradient(135deg, #fff5f5, #ffffff);
                        border-left: 4px solid #dc3545;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}